import { forwardRef, useState, type ChangeEvent, type InputHTMLAttributes } from 'react'
import { cn } from '../lib/cn'
import { FieldShell, fieldDescribedBy, useFieldId, type FieldChromeProps } from './field'

export type FileInputProps = Omit<FieldChromeProps, 'size'> &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> & {
    /**
     * Echo the chosen filename(s) under the control. **Default off** (audit
     * DOC-1): the native control already prints the name inside itself, so an
     * echo shows it twice. Turn it on for `multiple`, where the native control
     * collapses to "3 files" and the echo is the only way to see which. When
     * on, the line reserves its height so choosing a file cannot reflow an
     * `items-end` row.
     */
    showFileName?: boolean | undefined
  }

/**
 * Styled native file input (AUDIT_FIXES_JULY27 Item G): the browser's raw
 * "Choose File" button never matched sibling inputs' height or border. The
 * `file:` pseudo-element utilities restyle the chooser with semantic tokens
 * so the whole control sits flush with `inputClass()` siblings, and the
 * single `<label htmlFor>` (no aria-label, no wrapping label) is the one
 * accessible name — the old double-label axe finding.
 */
export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(function FileInput(
  { label, hideLabel, help, error, wrapperClassName, id, className, required, showFileName = false, onChange, ...rest },
  ref,
) {
  const fieldId = useFieldId(id)
  const [fileNames, setFileNames] = useState<string[]>([])
  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    setFileNames(Array.from(event.target.files ?? []).map((file) => file.name))
    onChange?.(event)
  }
  return (
    <FieldShell
      id={fieldId}
      label={label}
      hideLabel={hideLabel}
      help={help}
      error={error}
      required={required}
      wrapperClassName={wrapperClassName}
    >
      <input
        ref={ref}
        id={fieldId}
        type="file"
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={fieldDescribedBy(fieldId, help, error)}
        onChange={handleChange}
        className={cn(
          // The wrapper box mirrors inputClass('default') minus horizontal
          // padding (the chooser button supplies its own), so the control's
          // height and border sit flush with sibling inputs on an items-end row.
          'block w-full cursor-pointer rounded-md border bg-background text-sm text-muted-foreground',
          'file:mr-3 file:cursor-pointer file:rounded-l-md file:border-0 file:border-r file:border-solid file:border-border',
          'file:bg-muted file:px-3 file:py-2 file:text-sm file:font-medium file:text-foreground',
          'hover:file:bg-accent disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      />
      {/*
        Rendered whenever the echo is enabled, empty or not: gating on
        `fileNames.length` made the row grow the instant a file was chosen,
        pushing the sibling submit button out of line on an `items-end` row
        (audit DOC-1). min-h-4 matches text-xs's line box, so the space is
        reserved up front and the selection changes only the text.
      */}
      {showFileName && (
        <p className="mt-1 min-h-4 truncate text-xs text-muted-foreground">{fileNames.join(', ')}</p>
      )}
    </FieldShell>
  )
})
